import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let csrfToken;
let csrfPending;

async function getCsrfToken() {
  if (csrfToken !== undefined) return csrfToken;
  if (!csrfPending) {
    csrfPending = api.get("/auth/csrf", { timeout: 10000 })
      .then(({ data }) => { csrfToken = data.csrf_token; return csrfToken; })
      .finally(() => { csrfPending = null; });
  }
  return csrfPending;
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (!["get", "head", "options"].includes(method)) {
    config.headers["X-Vale-Request"] = "1";
    const token = await getCsrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
    else delete config.headers["X-CSRF-Token"];
  }
  return config;
});

api.interceptors.response.use((response) => {
  if (["/auth/session", "/auth/logout"].includes(response.config.url)) csrfToken = undefined;
  return response;
}, async (error) => {
  const config = error.config;
  // A different tab may have changed the session. Retry only a rejected CSRF
  // check (the mutation has not run), once, with the current session's token.
  if (error.response?.status === 403 && error.response.data?.detail === "csrf_invalid" && config && !config.csrfRetried) {
    csrfToken = undefined;
    config.csrfRetried = true;
    return api.request(config);
  }
  return Promise.reject(error);
});

export default api;

// fetch is used only for incremental response streaming, with the same CSRF
// contract as Axios. Never send the managed/provider API keys to the browser.
export async function streamApi(path, body, signal, onEvent) {
  let response;
  for (let attempt = 0; attempt < 2; attempt++) {
    const token = await getCsrfToken();
    response = await fetch(`${API}${path}`, { method: "POST", credentials: "include", signal,
      headers: { "Content-Type": "application/json", "X-Vale-Request": "1", ...(token ? { "X-CSRF-Token": token } : {}) }, body: JSON.stringify(body) });
    if (response.ok) break;
    const error = await response.json().catch(() => ({}));
    if (response.status === 403 && error.detail === "csrf_invalid" && attempt === 0) { csrfToken = undefined; continue; }
    throw new Error(typeof error.detail === "string" ? error.detail : "ai_unavailable");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "", finished = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split("\n\n"); buffer = blocks.pop();
      for (const block of blocks) {
        const line = block.split("\n").find((entry) => entry.startsWith("data: "));
        if (!line) continue;
        const event = JSON.parse(line.slice(6));
        if (event.type === "error") throw new Error(event.code);
        onEvent(event);
        if (event.type === "done") finished = true;
      }
    }
    if (!finished) throw new Error("ai_interrupted");
  } finally { await reader.cancel().catch(() => {}); }
}
